<template>
	<CHeaderLayout @show-account-menu="$emit('show-account-menu')">
		<template #title>
			<h1>Guest Access</h1>
			<div :class="{'flex align-center gap-4 mt-4': isMobile}">
				<label v-if="isMobile" class="sz-5 heavy" for="guest-access-switch">Enable Guest Access</label>
				<span @click="tryShowGuestUpsellDialog">
					<CSwitch
						v-if="!loading"
						id="guest-access-switch"
						v-model="guestPortalEnabled"
						:disabled="!globalStore.organization.Plan.AllowGuests"
						data-testid="guest-access-switch"
						class="inline-block"
						@input="onGuestPortalEnabledUpdated"
					/>
				</span>
			</div>
			<span v-if="loading" class="skeleton organization-guest-access-view__skeleton-switch"></span>

			<CGuestAccessUpsellContainer
				:show="showGuestUpsellDialog"
				context="ViewSettings"
				@close="showGuestUpsellDialog = false"
			/>
		</template>

		<main class="organization-settings-guest-access">
			<COrganizationGuestAccessView
				:model="model"
				:global-store="globalStore"
				@guest-portal-enabled-updated="onGuestPortalEnabledUpdated"
				@privacy-policy-updated="onPrivacyPolicyUpdated"
				@tos-updated="onTosUpdated"
				@guest-list-updated="onGuestListUpdated"
				@portal-message-updated="onPortalMessageUpdated"
				@profile-editing-updated="onProfileEditingUpdated"
				@registration-updated="onRegistrationUpdated"
			/>
		</main>
	</CHeaderLayout>
</template>
<script lang="ts">
	import { defineComponent, ref, inject } from 'vue';
	import {
		getGuestAccessViewModel,
		updatePrivacyPolicy,
		updateTermsOfService,
		updateGuestList,
		updatePortalMessage,
		updateGuestPortalEnabled,
		updateGuestProfileEditing,
		updateGuestRegistration
	} from '@cognitoforms/api/services/settings/guest-access-service';
	import COrganizationGuestAccessView from './OrganizationGuestAccessView.vue';
	import CHeaderLayout from 'src/components/app/HeaderLayout.vue';
	import CSwitch from 'src/components/form-controls/Switch.vue';
	import notify from 'src/utilities/notify';
	import type { GuestAccessViewModel } from '@cognitoforms/types/server-types/core/model/profile/guest-access-view-model';
	import { globalStore } from 'src/stores/global-store';
	import { websocketConnection } from 'src/utilities/websocket/websocket-connection-factory';
	import { isMobileKey } from 'src/utilities/injection-keys';
	import CGuestAccessUpsellContainer from 'src/components/dialogs/guests/GuestAccessUpsellContainer.vue';

	export type UpdateGuestListEvent = { formId: string; enabled: boolean; canEditProfile: boolean; canRegister: boolean; };
	export type UpdatePortalMessageEvent = { body: string, header: string };
	export type UpdateEditProfileEvent = { formId: string, enabled: boolean };
	export type UpdateRegistrationEvent = { formId: string, enabled: boolean };

	export default defineComponent({
		components: { COrganizationGuestAccessView, CHeaderLayout, CSwitch, CGuestAccessUpsellContainer },
		emits: ['show-account-menu'],
		setup() {
			const model = ref<GuestAccessViewModel>(null);
			const guestPortalEnabled = ref<boolean>(false);
			const loading = ref(true);
			const showGuestUpsellDialog = ref(false);

			getGuestAccessViewModel().then(res => {
				const portalEnabled = res.GuestPortalEnabled && globalStore.organization.Plan.AllowGuests;
				model.value = { ...res, GuestPortalEnabled: portalEnabled };
				guestPortalEnabled.value = portalEnabled;
				loading.value = false;
			});

			const onGuestPortalEnabledUpdated = async (enabled: boolean) => {
				if (!globalStore.organization.Plan.AllowGuests) {
					model.value.GuestPortalEnabled = guestPortalEnabled.value = false;
					return;
				}

				try {
					await updateGuestPortalEnabled(enabled);
					model.value.GuestPortalEnabled = enabled;
					guestPortalEnabled.value = enabled;
				}
				catch {
					notify(`An error occurred ${enabled ? 'enabling' : 'disabling'} guest access.`, 'error');
				}
			};

			const updateLocalGuestListModel = (
				formId: string,
				enabled: boolean,
				canEditProfile: boolean | undefined,
				canRegister: boolean | undefined
			) => {
				const guestList = model.value.GuestLists.find(g => g.Id === formId);
				guestList.Enabled = enabled;

				if (canEditProfile !== undefined)
					guestList.CanEditProfile = canEditProfile;

				if (canRegister !== undefined)
					guestList.CanRegister = canRegister;
			};

			const onGuestListUpdated = async (event: UpdateGuestListEvent) => {
				// Store the previous state of the guest list to revert in case of an error.
				const previousValue = { ...model.value.GuestLists.find(g => g.Id === event.formId) };

				try {
					websocketConnection.waitForEvent('Form', event.formId).then(() => {
						getGuestAccessViewModel().then(res => (model.value.UsageByPeriod = res.UsageByPeriod));
					});

					// Update the local model to reflect the changes.
					updateLocalGuestListModel(event.formId, event.enabled, event.canEditProfile, event.canRegister);

					await updateGuestList(event.formId, event.enabled, event.canEditProfile, event.canRegister);
				}
				catch {
					// If the update fails, revert the model to the previous state.
					updateLocalGuestListModel(previousValue.Id, previousValue.Enabled, previousValue.CanEditProfile, previousValue.CanRegister);
					notify('An error occurred while updating this guest list.', 'error');
				}
			};

			const onPrivacyPolicyUpdated = async (url: string) => {
				try {
					await updatePrivacyPolicy(url);
					notify('Privacy Policy Updated.', 'success');
					model.value.PrivacyPolicyUrl = url;
				}
				catch {
					notify('An error occurred updating your privacy policy.', 'error');
				}
			};

			const onTosUpdated = async (url: string) => {
				try {
					await updateTermsOfService(url);
					notify('Terms of Service Updated.', 'success');
					model.value.TermsOfServiceUrl = url;
				}
				catch {
					notify('An error occurred updating your terms of service.', 'error');
				}
			};

			const onPortalMessageUpdated = async (event: UpdatePortalMessageEvent) => {
				try {
					await updatePortalMessage(event.header, event.body);
					notify('Welcome message updated.', 'success');
					model.value.PortalMessageHeader = event.header;
					model.value.PortalMessageBody = event.body;
				}
				catch {
					notify('An error occurred while updating the portal message.', 'error');
				}
			};

			const onProfileEditingUpdated = async (event: UpdateEditProfileEvent) => {
				try {
					await updateGuestProfileEditing(event.formId, event.enabled);
					model.value.GuestLists.find(g => g.Id === event.formId).CanEditProfile = event.enabled;
				}
				catch {
					notify('An error occurred updating your edit profile settings.', 'error');
				}
			};

			const onRegistrationUpdated = async (event: UpdateRegistrationEvent) => {
				try {
					await updateGuestRegistration(event.formId, event.enabled);
					model.value.GuestLists.find(g => g.Id === event.formId).CanRegister = event.enabled;
				}
				catch {
					notify('An error occurred updating your registration settings.', 'error');
				}
			};

			const tryShowGuestUpsellDialog = () => {
				if (!globalStore.organization.Plan.AllowGuests) {
					showGuestUpsellDialog.value = true;
				}
			};

			return {
				model,
				guestPortalEnabled,
				onGuestPortalEnabledUpdated,
				onPrivacyPolicyUpdated,
				onTosUpdated,
				onGuestListUpdated,
				onPortalMessageUpdated,
				globalStore,
				loading,
				isMobile: inject(isMobileKey),
				showGuestUpsellDialog,
				tryShowGuestUpsellDialog,
				onProfileEditingUpdated,
				onRegistrationUpdated
			};
		},
		head() {
			return {
				title: 'Guest Access - Cognito Forms',
				titleTemplate: '%s'
			};
		}
	});
</script>